/**
 * Compatibility layer: Event + WeddingDetails <-> the legacy Wedding shape.
 *
 * This is what lets WeddingContext become a thin wrapper around
 * EventContext (see src/contexts/WeddingContext.tsx) without touching a
 * single line of the working Guests, Budget, Checklist, or RSVP features
 * — they all keep reading `useWedding()` exactly as before.
 *
 * WeddingDetails (partner names, ceremony info) is the wedding vertical's
 * own specialization table, so it doesn't belong in the generic
 * EventRepository. It's kept here, next to the adapter functions that are
 * its only consumers, as a small in-memory store for dummy mode.
 */
import { WEDDING_EVENT_TYPE, type Event, type EventLifecycleStage } from "@/types/domain/event";
import type { WeddingDetails } from "@/types/domain/wedding-details";
import type { CreateEventInput, UpdateEventInput } from "@/data/repositories/eventRepository";
import type { Wedding, WeddingInput, WeddingStage } from "@/types/wedding";
import { DUMMY_WEDDING_DETAILS } from "@/data/dummy/dummyEventData";

export function splitCoupleNames(coupleNames: string): { partnerOneName: string; partnerTwoName: string } {
  const [first, second] = coupleNames.split("&").map((part) => part.trim());
  return { partnerOneName: first ?? coupleNames, partnerTwoName: second ?? "" };
}

export function joinCoupleNames(details: Pick<WeddingDetails, "partnerOneName" | "partnerTwoName">): string {
  return details.partnerTwoName ? `${details.partnerOneName} & ${details.partnerTwoName}` : details.partnerOneName;
}

export function weddingStageToLifecycleStage(stage: WeddingStage): EventLifecycleStage {
  switch (stage) {
    case "inquiry":
      return "inquiry";
    case "booked":
      return "booked";
    case "planning":
      return "planning";
    case "final_month":
      return "final_review";
    case "completed":
      return "completed";
    case "archived":
      return "archived";
  }
}

export function lifecycleStageToWeddingStage(stage: EventLifecycleStage): WeddingStage {
  switch (stage) {
    case "inquiry":
    case "proposal":
      return "inquiry";
    case "booked":
      return "booked";
    case "planning":
      return "planning";
    case "production":
    case "final_review":
    case "event_day":
      return "final_month";
    case "completed":
      return "completed";
    case "archived":
      return "archived";
  }
}

function eventStatusForLifecycleStage(stage: EventLifecycleStage): Event["status"] {
  if (stage === "completed") return "completed";
  if (stage === "archived") return "archived";
  return "active";
}

/** In-memory WeddingDetails store for dummy mode — mirrors DummyEventRepository's own encapsulated array. */
class WeddingDetailsStore {
  private details: Map<string, WeddingDetails> = new Map(
    DUMMY_WEDDING_DETAILS.map((d) => [d.eventId, { ...d }])
  );

  get(eventId: string): WeddingDetails | undefined {
    return this.details.get(eventId);
  }

  upsert(eventId: string, patch: Partial<Omit<WeddingDetails, "eventId" | "createdAt">>): WeddingDetails {
    const now = new Date().toISOString();
    const existing = this.details.get(eventId);
    const next: WeddingDetails = existing
      ? { ...existing, ...patch, updatedAt: now }
      : {
          eventId,
          partnerOneName: patch.partnerOneName ?? "",
          partnerTwoName: patch.partnerTwoName ?? "",
          createdAt: now,
          updatedAt: now,
          ...patch,
        };
    this.details.set(eventId, next);
    return next;
  }
}

export const weddingDetailsStore = new WeddingDetailsStore();

/** Combine an Event + its WeddingDetails row into the legacy Wedding shape every existing wedding-scoped page reads. */
export function eventToWedding(event: Event): Wedding {
  const details = event.weddingDetails ?? weddingDetailsStore.get(event.id);
  return {
    id: event.id,
    organizationId: event.organizationId,
    coupleNames: event.title || (details ? joinCoupleNames(details) : "Untitled wedding"),
    partnerOneEmail: details?.partnerOneEmail,
    partnerOnePhone: details?.partnerOnePhone,
    partnerTwoEmail: details?.partnerTwoEmail,
    partnerTwoPhone: details?.partnerTwoPhone,
    weddingDate: event.startDate,
    venueName: event.venueName,
    venueCity: event.venueCity,
    stage: lifecycleStageToWeddingStage(event.lifecycleStage),
    coverImageUrl: event.coverImageUrl,
    guestCountEstimate: event.guestCountEstimate,
    budgetTotal: event.budgetEstimate,
    createdAt: event.createdAt,
    suspendedAt: event.suspendedAt,
    deletedAt: event.deletedAt,
    purgeAfter: event.purgeAfter,
  };
}

/** Translate the Wedding-shaped create form into a generic CreateEventInput, and stash the wedding-specific fields in WeddingDetails. */
export function weddingInputToCreateEventInput(
  input: WeddingInput,
  organizationId: string,
  createdBy: string
): CreateEventInput {
  const lifecycleStage = weddingStageToLifecycleStage(input.stage);
  return {
    organizationId,
    eventTypeId: WEDDING_EVENT_TYPE.id,
    title: input.coupleNames,
    status: eventStatusForLifecycleStage(lifecycleStage),
    lifecycleStage,
    startDate: input.weddingDate,
    timezone: "America/Mexico_City",
    currency: "MXN",
    createdBy,
    venueName: input.venueName,
    venueCity: input.venueCity,
    guestCountEstimate: input.guestCountEstimate,
    budgetEstimate: input.budgetTotal,
    weddingDetails: {
      ...splitCoupleNames(input.coupleNames),
      partnerOneEmail: input.partnerOneEmail,
      partnerOnePhone: input.partnerOnePhone,
      partnerTwoEmail: input.partnerTwoEmail,
      partnerTwoPhone: input.partnerTwoPhone,
    },
  };
}

/** Translate a partial Wedding-shaped edit into a generic UpdateEventInput. */
export function weddingPatchToUpdateEventInput(patch: Partial<WeddingInput>): UpdateEventInput {
  const result: UpdateEventInput = {};
  if (patch.coupleNames !== undefined) result.title = patch.coupleNames;
  if (patch.weddingDate !== undefined) result.startDate = patch.weddingDate;
  if (patch.venueName !== undefined) result.venueName = patch.venueName;
  if (patch.venueCity !== undefined) result.venueCity = patch.venueCity;
  if (patch.guestCountEstimate !== undefined) result.guestCountEstimate = patch.guestCountEstimate;
  if (patch.budgetTotal !== undefined) result.budgetEstimate = patch.budgetTotal;
  const contactChanged = patch.coupleNames !== undefined || patch.partnerOneEmail !== undefined || patch.partnerOnePhone !== undefined || patch.partnerTwoEmail !== undefined || patch.partnerTwoPhone !== undefined;
  if (contactChanged) {
    result.weddingDetails = {
      ...(patch.coupleNames !== undefined ? splitCoupleNames(patch.coupleNames) : {}),
      ...(patch.partnerOneEmail !== undefined && { partnerOneEmail: patch.partnerOneEmail }),
      ...(patch.partnerOnePhone !== undefined && { partnerOnePhone: patch.partnerOnePhone }),
      ...(patch.partnerTwoEmail !== undefined && { partnerTwoEmail: patch.partnerTwoEmail }),
      ...(patch.partnerTwoPhone !== undefined && { partnerTwoPhone: patch.partnerTwoPhone }),
    };
  }
  if (patch.stage !== undefined) {
    result.lifecycleStage = weddingStageToLifecycleStage(patch.stage);
    result.status = eventStatusForLifecycleStage(result.lifecycleStage);
  }
  return result;
}
